/**
 * WHAT THIS BROWSER REMEMBERS ABOUT WHOSE PUSH IT CARRIES.
 *
 * Two records, both local, both about the shared-browser case:
 *
 *   `hq.push.device-owner` — the endpoint this browser subscribed, and the
 *      person it was subscribed for. Written when this browser subscribes, and
 *      whenever the server confirms the held subscription is the signed-in
 *      person's. Bound to the endpoint: a record for an endpoint the browser
 *      no longer holds says nothing about the one it holds now.
 *   `hq.push.last-user` — the person who was last signed in ON THIS BROWSER.
 *      Says nothing about any endpoint; it is what makes "the account changed
 *      here" survive a page load, so the store's second drop rule is not dead
 *      on the first load of every fresh page (a browser restart, a reopened
 *      tab, a hard refresh — the ordinary shared-browser story).
 *
 * A browser keeps its PushSubscription across a sign-out. The server is the
 * authority on whose row that endpoint is — but the server can fail to
 * answer, and "unanswered" is deliberately NOT read as "not yours" (that would
 * turn any API hiccup into a push outage for everyone). Local evidence closes
 * the hole without that trade-off, when there IS local evidence.
 *
 * WHAT IS NOT CLOSED, exactly. Neither record exists for a subscription made
 * before this build shipped; both are gone after a site-data clear or a Safari
 * ITP eviction that leaves the service-worker subscription alive; and a
 * browser that refuses storage writes neither. On such a browser, a held
 * subscription that the server will not vouch for is KEPT and shown as
 * unconfirmed — never as on. That floor is deliberate (dropping on no evidence
 * would make every private window a push outage), and it is a floor, not a
 * closed case: say so rather than claiming the hole is shut "server or no
 * server".
 *
 * THREE ANSWERS, NOT TWO. A browser that refuses localStorage (private mode, a
 * blocked origin, cleared site data, a quota error) is NOT the same as a
 * browser that has no record: the first is "no evidence either way", the
 * second is "there is evidence, and it says nothing was ever claimed here".
 * Collapsing them into `null` was the fail-open: the caller could not tell
 * "this endpoint is unclaimed" from "the whole defence is unavailable", and
 * treated both as permission to keep the subscription. So the read reports
 * `unavailable` explicitly, and the write reports whether the record actually
 * landed — the caller decides what to do without evidence, and it does not
 * get to assume the best. (`NotificationSettingsStore` honours both: an
 * unreadable store and a refused write each raise
 * `localEvidenceUnavailable`, which the Notifications page says in words.)
 */

const KEY = "hq.push.device-owner";
const LAST_USER_KEY = "hq.push.last-user";

interface DeviceOwnerRecord {
  endpoint: string;
  userId: number;
}

/**
 * What the local record says about one endpoint.
 *   `owner`       — this browser recorded that endpoint as `userId`'s
 *   `none`        — storage is readable and holds no record for that endpoint
 *   `unavailable` — storage could not be read at all; NOT evidence of anything
 */
export type DeviceOwnerEvidence =
  | { kind: "owner"; userId: number }
  | { kind: "none" }
  | { kind: "unavailable" };

const storage = (): Storage | null => {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
};

/** What this browser remembers about `endpoint` — including "nothing readable". */
export function readDeviceOwner(endpoint: string): DeviceOwnerEvidence {
  const store = storage();
  if (!store) return { kind: "unavailable" };
  let raw: string | null;
  try {
    raw = store.getItem(KEY);
  } catch {
    // A store that throws on read is an unavailable store, not an empty one.
    return { kind: "unavailable" };
  }
  if (!raw) return { kind: "none" };
  try {
    const rec = JSON.parse(raw) as Partial<DeviceOwnerRecord> | null;
    // A record for a different endpoint, or a corrupt one, is readable
    // evidence that nothing was claimed for THIS endpoint.
    return rec && rec.endpoint === endpoint && typeof rec.userId === "number"
      ? { kind: "owner", userId: rec.userId }
      : { kind: "none" };
  } catch {
    return { kind: "none" };
  }
}

/** Records the endpoint as this person's. `false` when storage refused it. */
export function writeDeviceOwner(endpoint: string, userId: number): boolean {
  const store = storage();
  if (!store) return false;
  try {
    store.setItem(KEY, JSON.stringify({ endpoint, userId }));
    return true;
  } catch {
    // No local evidence, then; the caller must not assume there is any.
    return false;
  }
}

/**
 * Forget the record — but ONLY if it is the record about `endpoint`.
 *
 * The same rule as the unsubscribe this is called beside: an effect acts on the
 * thing the decision was about. This runs when a subscription has been let go
 * of, and a record naming a DIFFERENT endpoint is not about the one that went —
 * it is the evidence that arms drop rule (1) for whatever the browser is
 * holding now, which on a shared browser is the next person's. Wiping it
 * because some other endpoint was released takes the local defence away from a
 * subscription nobody asked about.
 *
 * A record that is unreadable or corrupt is left alone: it names no endpoint,
 * so it is not this endpoint's, and `readDeviceOwner` already reads it as
 * "there is evidence, and nothing was claimed for this endpoint".
 */
export function clearDeviceOwner(endpoint: string): void {
  try {
    const store = storage();
    if (!store) return;
    const raw = store.getItem(KEY);
    if (!raw) return;
    const rec = JSON.parse(raw) as Partial<DeviceOwnerRecord> | null;
    if (rec?.endpoint === endpoint) store.removeItem(KEY);
  } catch {
    // Nothing readable, nothing to clear.
  }
}

/**
 * Who was last signed in on this browser, across page loads. `null` covers
 * both "storage holds no marker" and "storage could not be read" — and here,
 * unlike the ownership record, they carry the same weight: neither is evidence
 * that the account changed, and only positive evidence ever drops anything.
 * The absence of this marker can therefore never cause a wrong drop, only a
 * missed one, which is the floor documented above.
 */
export function readLastSignedInUser(): number | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(LAST_USER_KEY);
    if (!raw) return null;
    const id = JSON.parse(raw) as unknown;
    // NOT `numericUserId` — deliberately. This is not a read of the auth
    // store: the marker is written by `writeLastSignedInUser(userId: number)`
    // in this module, from an id the caller has already put through
    // `auth/userId`, so a string here was written by nothing this app does.
    // Widening it would only make a foreign marker arm a DROP, and the floor
    // documented above is the other way round: a marker that cannot be read
    // costs a drop that would have happened, never a wrong one.
    return typeof id === "number" ? id : null;
  } catch {
    return null;
  }
}

/** Remembers the signed-in person for the next page load. `false` when storage refused. */
export function writeLastSignedInUser(userId: number): boolean {
  const store = storage();
  if (!store) return false;
  try {
    store.setItem(LAST_USER_KEY, JSON.stringify(userId));
    return true;
  } catch {
    return false;
  }
}
